/**
 * Data for the document screens, and the company switcher that scopes them.
 *
 * The hooks only assemble; every decision they make is in a tested pure
 * function elsewhere. That is deliberate: there is no React testing library in
 * this project, and the alternative to testing logic is not testing it.
 *
 * ONE ACTIVE BUSINESS, AND EVERY SCREEN FOLLOWS IT
 * -------------------------------------------------
 * The real backup carries two companies and the user switches between them.
 * Every query here is keyed on the resolved business id, so switching changes
 * the cache key rather than serving another company's cached answer. A hook that
 * ignored the active business would show the wrong company's invoices under the
 * current letterhead, which is a wrong GSTIN on a real document.
 *
 * This file previously resolved the business as `businesses.toArray()[0]` — the
 * first row that came back. That was a hardcoded constant before it (which is
 * why the list was empty after an import, because the importer namespaces every
 * business as `legacy:1.6.x:<companyId>:business`), and then an accident of key
 * ordering. Both were wrong for the same reason: the answer was never the user's.
 * It is now a stored preference, resolved and validated by `businessSession`.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { NormalizedDocument } from '@core/doc/types';
import type { BusinessIdentity } from '@core/doc/renderA4';
import { formatMoney } from '@core/money/format';
import { asMoney } from '@core/money/types';
import { docSpec } from '@domain/docTypes';
import { getDb } from '@data/db/database';
import {
  DocumentReadError,
  listRecentDocuments,
  loadDocument,
} from '@data/services/documentReader';
import type { RecentDocumentsPage } from '@data/services/documentReader';
import { BusinessNotConfiguredError, loadBusinessIdentityWithImages } from '@data/services/businessReader';
import { issueDraftDocument } from '@data/services/issueTransitionService';
import { currentDeviceId } from '@data/sync/deviceIdentity';

/** The local user, until real accounts exist. Matches the write services. */
const DEFAULT_USER = { id: 'local', name: 'You' };
import {
  listBusinesses,
  readActiveBusinessId,
  resolveActiveBusiness,
  setActiveBusinessId,
} from '@data/services/businessSession';
import type { Document } from '@data/db/types';

/**
 * The root of every query key that depends on which company is open.
 *
 * Exported so the import screen can invalidate the whole family after a commit,
 * and so a switch can invalidate exactly what a commit would have dirtied.
 * Hardcoding these strings in two files is how the two drift apart and an
 * invalidation silently stops matching — a stale cache with no error, which is
 * worse than a loud failure.
 */
export const DOCUMENTS_QUERY_ROOT = ['documents'] as const;
export const BUSINESSES_QUERY_ROOT = ['businesses'] as const;
/** Exact query key used by BusinessGatekeeper for the active business check. */
export const GATE_QUERY_KEY = ['businesses', 'active', 'gate'] as const;

/**
 * Every query root that holds business data, in one list.
 *
 * A RESTORE replaces the data underneath the running app. Any cached list that
 * survives it is still showing rows from before — and because those rows belong to
 * a specific business, a surviving cache after a company switch is a
 * cross-company data leak, not merely a stale display.
 *
 * So this list is enumerated in ONE place and invalidated wholesale, rather than
 * each caller remembering which queries it happens to know about. A cache root
 * added later and not added here is a leak waiting to happen.
 */
export const INVENTORY_QUERY_ROOTS = [
  BUSINESSES_QUERY_ROOT,
  ['documents'],
  ['parties'],
  ['products'],
  ['settings'],
  ['payments'],
] as const;

/** The active business, for any caller that needs it outside a component. */
export async function currentBusinessId(): Promise<string | null> {
  return readActiveBusinessId(getDb() as never);
}

/** Every company the user can switch to. */
export function useBusinesses() {
  return useQuery({
    queryKey: [...BUSINESSES_QUERY_ROOT, 'all'],
    queryFn: () => listBusinesses(getDb() as never),
  });
}

/** The company currently being worked in, or null before one is chosen. */
export function useActiveBusiness() {
  return useQuery({
    queryKey: [...BUSINESSES_QUERY_ROOT, 'active'],
    queryFn: () => readActiveBusinessId(getDb() as never),
  });
}

/**
 * Switch company.
 *
 * INVALIDATES EVERYTHING, not a named list of keys. Switching company changes
 * the meaning of every screen in the app, and a per-key list is a list that
 * goes stale the moment someone adds a query family and forgets to append it —
 * which is how the document list once served a cached empty answer forever.
 * Company switching is a rare, deliberate act, so paying a full refetch for
 * correctness is the right trade.
 *
 * `useParties` additionally carries the business id in its own query key, so it
 * would be correct even without this. Two mechanisms, one of them a backstop:
 * that is deliberate, because the failure is the wrong company's GSTIN on an
 * invoice and "it would be fine if everyone remembered" is not a safety story.
 */
export function useSwitchBusiness() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (businessId: string) => {
      await setActiveBusinessId(getDb() as never, businessId);
      return businessId;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries();
    },
  });
}

/**
 * The documents list, and how many there really are.
 *
 * The page size is 200, not 25. A cap is right — rendering 50,000 rows in a
 * sidebar is not — but 25 on a business with 471 imported documents showed the
 * user a sliver with no explanation, and "where are my other invoices" is the
 * question that gets asked when a list silently truncates. The count is returned
 * alongside so the screen can state the truncation rather than hide it.
 */
export const RECENT_DOCUMENTS_LIMIT = 200;

export function useRecentDocuments(
  limit = RECENT_DOCUMENTS_LIMIT,
  docType?: Document['docType'],
  offset = 0,
) {
  return useQuery({
    // Keyed on the RESOLVED id, so switching company changes the key rather than
    // serving the previous company's cached list.
    queryKey: [...DOCUMENTS_QUERY_ROOT, 'recent', 'current', limit, docType ?? 'ALL', offset],
    queryFn: async () => {
      const id = await currentBusinessId();
      // The no-business case still returns the full shape. Returning a partial
      // here is why `partyNamesByDocumentId` had to be optional at every call
      // site, and an optional field on a page of rows is exactly how a row ends
      // up printing "—" for a customer that exists.
      if (id === null) {
        return {
          documents: [],
          total: 0,
          truncated: false,
          partyNamesByDocumentId: new Map(),
        };
      }
      return listRecentDocuments(getDb() as never, id, limit, true, docType, offset) as Promise<RecentDocumentsPage>;
    },
  });
}

/**
 * Issue a DRAFT document: the transition that makes it a real tax document.
 *
 * Separate from `useDocument` on purpose. The read hook and the write hook share
 * a query key prefix so the invalidation below is total for the document family,
 * but they are different questions: one asks what a document IS, the other
 * changes what it is. A hook that did both would be a hook whose name had to
 * change the moment the lifecycle grew a state.
 */
export function useIssueDocument() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (documentId: string) => {
      const db = getDb();
      // Resolved OUTSIDE the transaction: the outbox records the device on every
      // mutation this issue publishes, and reading it here keeps `settings` out of
      // the document transaction.
      const deviceId = await currentDeviceId(db);
      return db.transaction(
        'rw',
        // `stockTransactions`, `products` and `documentLinks` are in scope because
        // ISSUING is the event that posts stock, and deciding WHICH lines the
        // document owns needs the link graph. Omitting one lets Dexie abort
        // mid-write and leaves a document issued with no movement — a state every
        // total still reconciles with.
        // `syncOutbox` is in scope for the same reason: an issue that commits
        // locally and is never published is a stock movement only this device
        // knows about.
        [db.documents, db.documentLines, db.auditLogs, db.stockTransactions, db.products, db.documentLinks, db.businesses, db.syncOutbox],
        () =>
          issueDraftDocument(db as never, documentId, DEFAULT_USER, new Date().toISOString(), deviceId),
      );
    },
    onSuccess: async () => {
      // The whole family, not one key: issuing changes the document, the recent
      // list (its status is rendered there), and the sequence's last-issued stamp.
      // Invalidating precisely is a list that goes stale the moment someone adds a
      // query family and forgets to append it.
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: DOCUMENTS_QUERY_ROOT }),
        queryClient.invalidateQueries({ queryKey: ['quotations'] }),
        queryClient.invalidateQueries({ queryKey: ['sales-orders'] }),
      ]);
    },
  });
}

export function useBusinessIdentity() {
  return useQuery({
    queryKey: [...BUSINESSES_QUERY_ROOT, 'identity', 'current'],
    queryFn: async () => {
      const id = await currentBusinessId();
      // Same refusal the reader makes, for the same reason, with the message the
      // reader would have used. A fresh install has no business row, and
      // `explainLoadFailure` already knows how to say that in words a user can act
      // on, so the error must be the type it checks for.
      if (id === null) {
        throw new BusinessNotConfiguredError(
          'No business is selected yet. Import a backup, or choose a company, before ' +
            'a document can be printed.',
        );
      }
      // The WITH-IMAGES reader, so a letterhead with a logo actually gets one.
      // `loadBusinessIdentity` resolves nothing and would silently omit both
      // images on every invoice - a field that is stored, selectable in the
      // profile, and never printed.
      return loadBusinessIdentityWithImages(getDb() as never, id);
    },
  });
}

/**
 * One document, scoped to the company currently open.
 *
 * The business id is passed to the reader so it REFUSES a document belonging to
 * another company. That is the whole point of Option A: a document id is a
 * global primary key, so without this the app will happily render the other
 * company's invoice inside the current letterhead.
 *
 * The state code also comes from the business rather than a constant. It was
 * hardcoded to '36' (Telangana) when there was one company and that was true;
 * with two it is a bug, because a second company in Karnataka would be treated as
 * Telangana when deciding INTRA vs INTER.
 */
export function useStoredDocument(documentId: string | null) {
  const business = useActiveBusiness();
  const businessId = business.data ?? null;
  return useQuery({
    queryKey: [...DOCUMENTS_QUERY_ROOT, 'stored', businessId, documentId],
    enabled: !!businessId && !!documentId,
    queryFn: async () => {
      const document = await getDb().documents.get(documentId as string);
      if (!document || document.businessId !== businessId) {
        throw new Error('This document does not belong to the active business.');
      }
      return document;
    },
  });
}

export function useDocument(documentId: string | null) {
  const activeBusiness = useActiveBusiness();
  const businessId = activeBusiness.data ?? null;
  return useQuery({
    // Keyed on the document AND the business, so switching company does not serve
    // the previous company's cached page for the same id.
    queryKey: [...DOCUMENTS_QUERY_ROOT, 'one', businessId, documentId],
    queryFn: async () => {
      const business = await resolveActiveBusiness(getDb() as never);
      return loadDocument(getDb() as never, documentId as string, {
        businessStateCode: business.stateCode,
        businessId: business.id,
      });
    },
    enabled: documentId !== null && businessId !== null,
    // Force refetch on mount to avoid stale preview after SPA navigation.
    // Global staleTime (60s) would otherwise return cached data.
    staleTime: 0,
  });
}

// ── The message shown to the user. Pure, and therefore testable. ─────────────

/**
 * Turn a read failure into something worth showing a shopkeeper.
 *
 * These failures are the interesting ones because the alternative — rendering a
 * half-built page — produces something that looks like a real invoice. So each
 * one gets its own message rather than a generic "something went wrong", and
 * each says what the user can actually do.
 */
export function explainLoadFailure(error: unknown): string {
  if (error instanceof DocumentReadError) {
    switch (error.code) {
      case 'NOT_FOUND':
        return 'That document no longer exists. It may have been deleted.';
      case 'NO_LINES':
        return 'This document has no line items, so it cannot be printed. It looks like an interrupted save.';
      case 'ORPHANED_LINE':
        return 'This document has inconsistent line items and will not be printed, because doing so could show the wrong items on the invoice.';
      case 'WRONG_BUSINESS':
        // The one message here that must name the company boundary explicitly,
        // because the user's likely reaction is "I just switched, why is this
        // missing?" — and the answer is that this document is not this company's.
        return 'That document belongs to a different company, so it cannot be opened while this one is active. Switch to the company it belongs to, or import it separately.';
      default:
        return 'This document could not be read.';
    }
  }
  if (error instanceof BusinessNotConfiguredError) {
    return 'No business details are set up yet, so a letterhead cannot be printed. Complete the business profile first.';
  }
  return 'Something went wrong loading this document.';
}

/** One line for the list. */
export function summarise(doc: Document): string {
  // `formatMoney`, not `toFixed`. Money formatting belongs to core so the list,
  // the document and the report cannot disagree about how a figure reads - and
  // an ESLint rule enforces it, because this is exactly the sort of thing that
  // gets re-implemented locally and then drifts.
  //
  // THE TYPE IS PART OF THE LINE, and leaving it out was a real defect: an
  // imported quotation and an imported sales invoice then rendered as the same
  // two strings — number and total — so a migration of 371 invoices and 46
  // quotations looked like 417 invoices. The data was correctly typed the whole
  // time; the LIST was the thing that could not tell them apart, and the user's
  // reasonable conclusion was that the import had been wrong.
  //
  // The label comes from the registry rather than a local table, because a
  // second list of document names is exactly the kind of thing that goes stale
  // when the eleventh type is added.
  return `${docSpec(doc.docType).shortLabel} · ${doc.docNumber} · ${formatMoney(
    asMoney(doc.grandTotalPaise),
  )}`;
}

export type DocumentBundle = { document: NormalizedDocument; business: BusinessIdentity };
